import React, { useState, useContext, useEffect, useMemo, useRef, useCallback } from "react";
import Cookies from "js-cookie";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { FiDownload, FiEdit , FiPhoneCall, FiSettings, FiTrendingUp, FiUserPlus, FiUsers } from "react-icons/fi";
import { IoChevronDownOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import AppContext from "../../context/AppContext";
import { useNavigate } from "react-router-dom";
import { listCcSupervisorOptions, listCcWorkspaceUsers } from "../../service/CallCenterUserAdmin";
import { getCallCenterApiErrorMessage } from "../../service/CallCenterApi";
import {
  ORG_CONTEXT_CHANGED_EVENT,
  ensureSuperAdminOrganizationContext,
} from "../../utils/organizationContext";
import { Spinner } from "../../Spinner";
import translations from "../../components/common/Translations";
import { getPermissionByScreenCode, hasScreenAccess } from "../../utils/permissions";

const SORT_BY_OPTIONS = [
  { value: "NAME", label: "Name" },
  { value: "EMAIL", label: "Email" },
  { value: "CREATED_AT", label: "Created" },
];

const SORT_DIR_OPTIONS = [
  { value: "ASC", label: "Ascending" },
  { value: "DESC", label: "Descending" },
];

const getRoleKind = (user) => {
  const raw = `${user?.roleCode || ""} ${user?.role || ""} ${user?.roleDisplayName || ""}`.toUpperCase();
  if (raw.includes("ADMIN") || raw.includes("SUPER")) return "admin";
  if (raw.includes("AGENT")) return "agent";
  return "other";
};

const roleBadgeClass = (kind) => {
  if (kind === "admin") return "bg-violet-50 text-violet-700";
  if (kind === "agent") return "bg-[#EEF4FF] text-[#2563EB]";
  return "bg-slate-100 text-slate-600";
};

const ModernSelect = ({ id, value, onChange, onFocus, children }) => (
  <div className="relative">
    <select
      id={id}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      className="h-10 appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-9 text-sm text-slate-700 outline-none transition focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15"
    >
      {children}
    </select>
    <IoChevronDownOutline className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
  </div>
);

const StatCard = ({ title, value, footer, icon, trend }) => (
  <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <p className="text-sm font-medium text-slate-500">{title}</p>
      <span className="flex h-8 w-8 items-center justify-center text-[#2B7FFF]">{icon}</span>
    </div>
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <p className="text-[28px] font-bold leading-none tracking-tight text-slate-900 tabular-nums">{value}</p>
      {trend != null && (
        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-600">
          ↑ {trend}
        </span>
      )}
    </div>
    <p className="mt-2 text-xs text-slate-400">{footer}</p>
  </div>
);

const UserList = () => {
  const navigate = useNavigate();
  const { theme, langMode, userType } = useContext(AppContext);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState("NAME");
  const [sortDir, setSortDir] = useState("ASC");
  const [supervisorFilter, setSupervisorFilter] = useState("");
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [supervisorsLoading, setSupervisorsLoading] = useState(false);
  const supervisorsLoadedRef = useRef(false);
  const supervisorsLoadingRef = useRef(false);
  const [userList, setUerList] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [listMeta, setListMeta] = useState({
    hasNext: undefined,
    hasPrevious: undefined,
    apiPageCount: 0,
  });
  const [loading, setLoading] = useState(false);

  const userPerm = getPermissionByScreenCode("USER_MANAGEMENT");
  const canUpdateUser =
    userType === "super_admin" || Boolean(userPerm?.canUpdate);
  const canCreateUser =
    userType === "super_admin" || hasScreenAccess("USER_MANAGEMENT", "canCreate");
  const showActionColumn = canUpdateUser;

  const apiPageCount =
    typeof listMeta.apiPageCount === "number" && listMeta.apiPageCount > 0
      ? listMeta.apiPageCount
      : 0;
  const lastPageIndex =
    totalValue <= 0
      ? 0
      : apiPageCount > 0
        ? Math.max(0, apiPageCount - 1)
        : Math.max(0, Math.ceil(totalValue / pageSize) - 1);

  const canGoPrev = currentPage > 0;
  const canGoNext =
    listMeta.hasNext === false
      ? false
      : listMeta.hasNext === true
        ? true
        : currentPage < lastPageIndex;

  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  const totalItems = totalValue;
  const isRtl = langMode === "ar";
  const organizationName =
    (typeof localStorage !== "undefined" && localStorage.getItem("selectedOrgName")) || "EEMAD";

  const stats = useMemo(() => {
    const agents = userList.filter((user) => getRoleKind(user) === "agent").length;
    const admins = userList.filter((user) => getRoleKind(user) === "admin").length;
    const active = userList.filter((user) => user.active).length;
    return { agents, admins, active };
  }, [userList]);

  const editInfo = (userId) => {
    navigate(`/add-user/${userId}?isEdit=true`);
  };

  const tableHeaders = [
    translations[langMode].userId,
    translations[langMode].Role,
    translations[langMode].username,
    translations[langMode].Email,
    translations[langMode].Phone,
    "Supervisor",
    ...(showActionColumn ? [translations[langMode].Action] : []),
  ];

  const handlePageSizeChange = (e) => {
    const raw = parseInt(e.target.value, 10);
    const size = Math.min(200, Math.max(1, Number.isFinite(raw) ? raw : 10));
    setPageSize(size);
    setCurrentPage(0);
  };

  const goToPreviousPage = () => {
    if (canGoPrev) setCurrentPage((p) => p - 1);
  };

  const goToNextPage = () => {
    if (canGoNext) setCurrentPage((p) => p + 1);
  };

  const loadSupervisorOptions = useCallback(async () => {
    if (supervisorsLoadedRef.current || supervisorsLoadingRef.current) {
      return;
    }
    supervisorsLoadingRef.current = true;
    setSupervisorsLoading(true);
    try {
      await ensureSuperAdminOrganizationContext();
      const supervisors = await listCcSupervisorOptions();
      setSupervisorOptions(Array.isArray(supervisors) ? supervisors : []);
      supervisorsLoadedRef.current = true;
    } catch {
      setSupervisorOptions([]);
    } finally {
      supervisorsLoadingRef.current = false;
      setSupervisorsLoading(false);
    }
  }, []);

  const handleSupervisorFilterFocus = () => {
    loadSupervisorOptions();
  };

  const fetchData = async (pageIndex, size, sortByParam, sortDirParam, supervisorUserId) => {
    try {
      if (!Cookies.get("accessToken")) {
        setUerList([]);
        setTotalValue(0);
        setListMeta({ hasNext: undefined, hasPrevious: undefined, apiPageCount: 0 });
        return;
      }
      setLoading(true);
      await ensureSuperAdminOrganizationContext();
      const data = await listCcWorkspaceUsers({
        page: pageIndex,
        size,
        sortBy: sortByParam,
        sortDir: sortDirParam,
        supervisorUserId: supervisorUserId || undefined,
      });
      setUerList(Array.isArray(data?.items) ? data.items : []);
      const totalElements =
        typeof data?.totalElements === "number" ? data.totalElements : 0;
      setTotalValue(totalElements);

      const apiTotalPages =
        typeof data?.totalPages === "number" ? data.totalPages : 0;
      setListMeta({
        hasNext: data?.hasNext,
        hasPrevious: data?.hasPrevious,
        apiPageCount: apiTotalPages,
      });
      setLoading(false);
    } catch (error) {
      const envelope = error?.envelope ?? error?.response?.data;
      const msg =
        envelope != null
          ? getCallCenterApiErrorMessage(envelope, translations[langMode].failedtofetchusers)
          : translations[langMode].failedtofetchusers;
      toast.error(msg);
      setUerList([]);
      setTotalValue(0);
      setListMeta({ hasNext: undefined, hasPrevious: undefined, apiPageCount: 0 });
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage, pageSize, sortBy, sortDir, supervisorFilter);
  }, [currentPage, pageSize, sortBy, sortDir, supervisorFilter]);

  useEffect(() => {
    const onOrgContextChanged = () => {
      setCurrentPage(0);
      setSupervisorFilter("");
      setSupervisorOptions([]);
      supervisorsLoadedRef.current = false;
      supervisorsLoadingRef.current = false;
      setSupervisorsLoading(false);
      fetchData(0, pageSize, sortBy, sortDir, "");
    };
    window.addEventListener(ORG_CONTEXT_CHANGED_EVENT, onOrgContextChanged);
    return () => window.removeEventListener(ORG_CONTEXT_CHANGED_EVENT, onOrgContextChanged);
  }, [pageSize, sortBy, sortDir]);

  const isPlatformSuperAdminUser = (user) =>
    String(user?.roleCode || user?.role || "").toUpperCase() === "SUPER_ADMIN"
    && (user?.organizationId == null || user?.organizationId === "");

  const handleExport = () => {
    const rows = [
      ["ID", "Role", "User Name", "Email", "Phone", "Supervisor", "Active"],
      ...userList.map((user) => [
        user.id,
        user.roleDisplayName || (user.roleCode || user.role || "").replace(/_/g, " "),
        user.name || "",
        user.email || "",
        user.agentExtension || "",
        user.supervisorName || "",
        user.active ? "Yes" : "No",
      ]),
    ];
    const csv = rows
      .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "user-management.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const rangeLabel =
    totalItems > 0
      ? `${startIndex + 1}–${Math.min(endIndex, totalItems)} of ${totalItems} ${translations[langMode].items}`
      : `0–0 of 0 ${translations[langMode].items}`;

  return (
    <div
      className={`min-h-full bg-[#f4f7fb] px-4 py-6  ${
        theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""
      }`}
    >
      <header className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1
            className="text-3xl font-bold tracking-tight text-slate-900"
            data-testid="user-list-title"
          >
            {translations[langMode].userlist}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Manage call agents and administrators for the {organizationName} workspace.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex h-10 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 shadow-sm transition hover:bg-slate-50"
          >
            <FiDownload className="h-4 w-4" />
            {translations[langMode].exportBtn}
          </button>
          {canCreateUser && (
            <button
              type="button"
              data-testid="add-user"
              onClick={() => navigate("/add-user")}
              className="inline-flex h-10 items-center gap-2 rounded-full bg-[#155DFC] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#236DB4]"
            >
              <FiUserPlus className="h-4 w-4" />
              {translations[langMode].addUser}
            </button>
          )}
        </div>
      </header>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Total Users"
          value={totalItems}
          footer={`Across the ${organizationName} workspace`}
          icon={<FiUsers className="h-5 w-5" />}
        />
        <StatCard
          title="Call Agents"
          value={stats.agents}
          footer="Handling outbound recovery"
          icon={<FiPhoneCall className="h-5 w-5" />}
        />
        <StatCard
          title="Administrators"
          value={stats.admins}
          footer="Full workspace access"
          icon={<FiSettings className="h-5 w-5" />}
        />
        <StatCard
          title="Active Today"
          value={stats.active}
          footer="Currently enabled accounts"
          icon={<FiTrendingUp className="h-5 w-5" />}
        />
      </div>

      <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-400">Sort by</label>
              <ModernSelect
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(0);
                }}
              >
                {SORT_BY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </ModernSelect>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-400">Direction</label>
              <ModernSelect
                value={sortDir}
                onChange={(e) => {
                  setSortDir(e.target.value);
                  setCurrentPage(0);
                }}
              >
                {SORT_DIR_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </ModernSelect>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-400">Supervisor</label>
              <ModernSelect
                value={supervisorFilter}
                onFocus={handleSupervisorFilterFocus}
                onChange={(e) => {
                  setSupervisorFilter(e.target.value);
                  setCurrentPage(0);
                }}
              >
                <option value="">
                  {supervisorsLoading ? "Loading supervisors…" : "All supervisors"}
                </option>
                {supervisorOptions
                  .filter((supervisor) => {
                    const id = supervisor.userId ?? supervisor.id;
                    return Number.isFinite(Number(id));
                  })
                  .map((supervisor) => {
                    const supervisorId = supervisor.userId ?? supervisor.id;
                    return (
                      <option key={supervisorId} value={supervisorId}>
                        {supervisor.name || supervisor.email || supervisorId}
                      </option>
                    );
                  })}
              </ModernSelect>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-400 lg:justify-end">
            <p className="flex items-center gap-1">
              {rangeLabel}
              {isRtl ? (
                <IoIosArrowForward
                  onClick={goToPreviousPage}
                  className={`cursor-pointer ${!canGoPrev ? "opacity-40" : "hover:text-slate-600"}`}
                />
              ) : (
                <IoIosArrowBack
                  onClick={goToPreviousPage}
                  className={`cursor-pointer ${!canGoPrev ? "opacity-40" : "hover:text-slate-600"}`}
                />
              )}
              {isRtl ? (
                <IoIosArrowBack
                  onClick={goToNextPage}
                  className={`cursor-pointer ${!canGoNext ? "opacity-40" : "hover:text-slate-600"}`}
                />
              ) : (
                <IoIosArrowForward
                  onClick={goToNextPage}
                  className={`cursor-pointer ${!canGoNext ? "opacity-40" : "hover:text-slate-600"}`}
                />
              )}
            </p>
            <div className="flex items-center gap-2">
              <label htmlFor="pageSize">{translations[langMode].rows}:</label>
              <ModernSelect id="pageSize" value={pageSize} onChange={handlePageSizeChange}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </ModernSelect>
            </div>
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <div className="relative max-h-[520px] overflow-y-auto">
            <table className="min-w-full text-left text-sm" data-testid="user-list-table">
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="border-b border-slate-100">
                  {tableHeaders.map((header) => (
                    <th
                      key={header}
                      scope="col"
                      className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              {loading && <Spinner />}
              <tbody>
                {userList && userList.length > 0 ? (
                  userList.map((user) => {
                    const kind = getRoleKind(user);
                    const roleLabel =
                      user.roleDisplayName ||
                      (user.roleCode || user.role || "").replace(/_/g, " ");
                    return (
                      <tr key={user.id} className="border-b border-slate-100 last:border-0">
                        <td className="whitespace-nowrap px-4 py-4">
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-block h-2.5 w-2.5 rounded-full ${
                                user.active ? "bg-emerald-500" : "bg-red-500"
                              }`}
                              title={user.active ? "Active" : "Inactive"}
                            />
                            <span className="font-medium text-slate-800">{user.id}</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-4">
                          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${roleBadgeClass(kind)}`}>
                            {roleLabel}
                          </span>
                          {isPlatformSuperAdminUser(user) && (
                            <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
                              Platform
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 font-medium text-slate-800">
                          {user.name}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-slate-500">
                          {user.email || "—"}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-slate-500">
                          {user.agentExtension || "—"}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-slate-500">
                          {user.supervisorName || "—"}
                        </td>
                        {showActionColumn && (
                          <td className="whitespace-nowrap px-4 py-4">
                            <button
                              type="button"
                              onClick={() => editInfo(user.id)}
                              title={translations[langMode].editandupdateuserdetail}
                              className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 transition hover:bg-emerald-100"
                            >
                              <FiEdit  className="h-4 w-4" />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={tableHeaders.length}
                      className="py-10 text-center text-slate-400"
                      data-testid="no-users-found-text"
                    >
                      {translations[langMode].nowuserfound}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserList;
